import { useQuery } from "@tanstack/react-query";
import { fxMonthConverter } from "@talentkontor/domain";
import { api, emptyIfForbidden } from "./index";

/** Gemeinsame Abfragen je Agentur. Jede Seite nimmt, was sie braucht; RLS begrenzt, was ankommt. */

export interface TalentRow {
  id: string;
  org_id: string;
  talent_number: number | null;
  first_name: string;
  last_name: string | null;
  display_name: string | null;
  email: string | null;
  country: string | null;
  city: string | null;
  small_business: boolean;
  commission_bp: number | null;
  status: string;
  instagram_handle: string | null;
  created_at: string;
}
export interface BrandRow {
  id: string;
  org_id: string;
  name: string;
  legal_name: string | null;
  country: string | null;
  invoice_email: string | null;
  payment_terms_days: number | null;
  language: string | null;
  created_at: string;
  /** Rechnungsanschrift und Weg (0018, 0039); in Listen mit select * vorhanden */
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  vat_id?: string | null;
  register_info?: string | null;
  kind?: string | null;
  represented_by?: string | null;
  invoice_via_agency?: boolean | null;
  invoice_route?: "via_agency" | "direct_to_brand" | null;
  /** 0066: Rechnungen zusätzlich als E-Rechnung (XRechnung) senden; Käuferreferenz bzw. Leitweg-ID */
  e_invoice?: boolean | null;
  buyer_reference?: string | null;
}
export interface DealRow {
  id: string;
  org_id: string;
  deal_number: number | null;
  talent_id: string;
  brand_id: string;
  title: string;
  model: string;
  status: string;
  currency: string;
  budget_cents: number | null;
  commission_bp: number | null;
  start_month: string | null;
  months: number | null;
  deliverables: string | null;
  platforms: string[];
  usage_rights: string | null;
  content_deadline: string | null;
  archived_at: string | null;
  created_at: string;
  /** Nachprovision nach Austritt (0074): Regel an der umgestellten Kooperation, Satz je Abrechnungsmonat */
  post_term?: unknown;
  post_term_from?: string | null;
  post_term_until?: string | null;
}
export interface PeriodRow {
  id: string;
  org_id: string;
  deal_id: string;
  month: string;
  amount_cents: number | null;
  status: string;
  manual_status: string | null;
  content_deadline: string | null;
  commission_forecast_cents: number | null;
  invoice_requested_at: string | null;
  booked_at: string | null;
  /** 0064: Stand aus dem bisherigen System (abgerechnet bzw. bezahlt, ohne Rechnung im Portal) */
  imported_state?: "invoiced" | "brand_paid" | "paid" | null;
  /** 0060: Ausgang des Monats (Storno, Ausfallhonorar, nicht abgenommen) und das vereinbarte Honorar davor */
  outcome?: "cancelled" | "cancellation_fee" | "not_accepted_reduced" | "not_accepted_fee" | null;
  original_amount_cents?: number | null;
  cancellation_fee_cents?: number | null;
}
export interface LineItemRow {
  id: string;
  deal_id: string;
  period_id: string | null;
  kind: string;
  description: string;
  amount_cents: number;
  commissionable: boolean;
}
export interface AgencyTermsRow {
  deal_id: string;
  agency_fee_mode: string | null;
  agency_fee_bp: number | null;
  agency_fee_cents: number | null;
}
export interface InvoiceRow {
  id: string;
  org_id: string;
  kind: "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent";
  /** 0043: Stornorechnung zu, Grund, eingegangene Summe; 0046: Gutschrift der Agentur, Widerspruch */
  credit_for?: string | null;
  /** 0198: Beleg über Verzugskosten zu dieser Rechnung */
  costs_for?: string | null;
  cancel_reason?: string | null;
  paid_cents?: number | null;
  self_billed?: boolean | null;
  objected_at?: string | null;
  objection_reason?: string | null;
  /** 0045: PDF aus den Angaben im Portal erzeugt oder eigene Datei des Creators (mit Bestätigung von Nummer und Betrag) */
  pdf_origin?: "generated" | "uploaded" | null;
  pdf_confirmed_at?: string | null;
  /** 0064: aus dem bisherigen System übernommen (fremde Nummer, kein im Portal erzeugtes PDF) */
  imported?: boolean | null;
  imported_at?: string | null;
  import_note?: string | null;
  deal_id: string | null;
  period_id: string | null;
  talent_id: string | null;
  brand_id: string | null;
  number: string | null;
  issue_date: string | null;
  currency: string;
  net_cents: number | null;
  tax_cents: number | null;
  gross_cents: number | null;
  status: string;
  dunning_stage: string;
  dunning_blocked: boolean;
  in_collection: boolean;
  revenue_month: string | null;
  document_due_date: string | null;
  effective_due_date: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  sent_at: string | null;
  paid_at: string | null;
  last_dunning_at: string | null;
  /** Auszahlung an den Creator vermerkt (0012); Zugang der Rechnung und letzter Rückläufer (0055/0085) */
  admin_payment_confirmed_at?: string | null;
  delivered_at?: string | null;
  delivery_failed_at?: string | null;
  /** 0180: letzte Mail an die Marke zu dieser Rechnung gescheitert (Rechnung, Storno, Mahnung) und ihre Art */
  mail_failed_at?: string | null;
  mail_failed_text_key?: string | null;
  updated_at: string;
  created_at: string;
  [key: string]: unknown;
}
export interface OrgSettingsRow {
  org_id: string;
  legal_name: string | null;
  currency: string;
  currencies: string[] | null;
  locale: string;
  timezone: string;
  default_commission_bp: number | null;
  agency_fee_mode: string | null;
  agency_fee_bp: number | null;
  agency_fee_cents: number | null;
  payment_terms_days: number | null;
  invoice_route: string | null;
  [key: string]: unknown;
}

const byOrg = (orgId: string) => [["org_id", "eq", orgId]] as const;

export const useTalents = (orgId: string) =>
  useQuery({ queryKey: ["talents", orgId], queryFn: () => api.select<TalentRow>("talents", { filters: byOrg(orgId), order: [["first_name", "asc"]] }) });
export const useBrands = (orgId: string) =>
  useQuery({ queryKey: ["brands", orgId], queryFn: () => api.select<BrandRow>("brands", { filters: byOrg(orgId), order: [["name", "asc"]] }) });
export const useDeals = (orgId: string) =>
  useQuery({ queryKey: ["deals", orgId], queryFn: () => api.select<DealRow>("deals", { filters: byOrg(orgId), order: [["created_at", "desc"]] }) });
export const usePeriods = (orgId: string) =>
  useQuery({ queryKey: ["deal_periods", orgId], queryFn: () => api.select<PeriodRow>("deal_periods", { filters: byOrg(orgId), order: [["month", "asc"]] }) });
export const useLineItems = (orgId: string) =>
  useQuery({ queryKey: ["deal_line_items", orgId], queryFn: () => api.select<LineItemRow>("deal_line_items", { filters: byOrg(orgId) }) });
export const useAgencyTerms = (orgId: string) =>
  useQuery({
    queryKey: ["deal_agency_terms", orgId],
    // Interne Konditionen: wer sie nicht lesen darf, bekommt eine leere Liste; ein Ladefehler bleibt ein Fehler (AD-016)
    queryFn: () => emptyIfForbidden(api.select<AgencyTermsRow>("deal_agency_terms", { filters: byOrg(orgId) })),
  });
/**
 * Vertragsstand je Kooperation (für den Wächter „abgerechnet ohne unterschriebenen Vertrag“); Ende und Verlängerung für
 * VERTRAEGE-R2-05, Ursprung eines Nachtrags (R3-02) und Art und Tag einer Aufhebung (R3-03)
 */
export type ContractStateRow = { id: string; deal_id: string | null; status: string; ends_on?: string | null; auto_renew?: boolean | null;
  amends_id?: string | null; void_kind?: string | null; voided_at?: string | null; completed_at?: string | null };
export const useContractStates = (orgId: string) =>
  useQuery({
    queryKey: ["contracts", orgId, "states"],
    queryFn: () => emptyIfForbidden(api.select<ContractStateRow>("contracts", { columns: "id, deal_id, status, ends_on, auto_renew, amends_id, void_kind, voided_at, completed_at", filters: byOrg(orgId) })),
  });
export const useInvoices = (orgId: string) =>
  useQuery({ queryKey: ["invoices", orgId], queryFn: () => api.select<InvoiceRow>("invoices", { filters: byOrg(orgId), order: [["issue_date", "desc"]] }) });
export const useOrgSettings = (orgId: string) =>
  useQuery({
    queryKey: ["org_settings", orgId],
    queryFn: async () => (await api.select<OrgSettingsRow>("org_settings", { filters: byOrg(orgId) }))[0] ?? null,
  });

export function talentName(t: Pick<TalentRow, "display_name" | "first_name" | "last_name"> | undefined): string {
  if (!t) return "–";
  return t.display_name ?? `${t.first_name} ${t.last_name ?? ""}`.trim();
}

export interface BookingRow {
  id: string;
  org_id: string;
  deal_id: string | null;
  period_id: string | null;
  talent_id: string | null;
  brand_id: string | null;
  kind: string;
  booked_on: string;
  amount_cents: number;
  currency: string;
  talent_share_cents: number | null;
  agency_share_cents: number | null;
  fx_rate_to_org?: number | null;
  amount_org_cents?: number | null;
  visible_to_talent: boolean;
  status: string;
  note: string | null;
}
export interface FxRow {
  base: string;
  quote: string;
  valid_on: string;
  rate: number;
}

export const useBookings = (orgId: string) =>
  useQuery({
    queryKey: ["bookings", orgId],
    // Buchungen sind Finanzdaten; ohne Leserecht bleibt die Liste leer, ein Ladefehler bleibt ein Fehler (AD-016)
    queryFn: () => emptyIfForbidden(api.select<BookingRow>("bookings", { filters: byOrg(orgId), order: [["booked_on", "desc"]] })),
  });
// Ein Kurs je Monat (Sicht fx_rates_monthly, 0076): Tageskurse würden die Zeilengrenze der Schnittstelle überschreiten
export const useFxRates = () =>
  useQuery({ queryKey: ["fx_rates"], queryFn: () => api.select<FxRow>("fx_rates_monthly", { order: [["valid_on", "asc"]] }), staleTime: 60 * 60_000 });

/** Umrechnung mit dem letzten Kurs bis Monatsende, direkt, umgekehrt oder über den Euro (wie app.fx_rate); null ohne Kurs. */
export function fxConverter(rates: readonly FxRow[]) {
  return fxMonthConverter(rates);
}
